import styled from "styled-components";
import theme from "../../style/theme";
import { TaskPayload } from "../../types/routing-service/task";
import { RoutingServicePostCodeButton } from "../button/RoutingServicePostCodeButton";
import { RoutingServiceTime2 } from "../date/RoutingServiceTime2";
import AddIcon from "../icons/AddIcon";
import DeleteIcon from "../icons/DeleteIcon";
import { GeneralTextField } from "../text-field/GeneralTextField";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragHandleIcon from '@mui/icons-material/DragHandle';

interface Props {
    index: number;
    task: TaskPayload;
    pickUpAddressDiabled: boolean;
    editTask: (index: number, task: TaskPayload) => void;
    showAdd: boolean;
    showDelete: boolean;
    clickAdd: () => void;
    clickDelete: (index: number) => void;
}

export const RoutingServiceTaskTableRow: React.FC<Props> = (
    {
        index,
        task,
        pickUpAddressDiabled,
        editTask,
        showAdd,
        showDelete,
        clickAdd,
        clickDelete,
    }
) => {
    const editPickUpPlace = (placeName: string) => {
        editTask(index, {
            ...task,
            pickUpPlace: {
                placeName,
                road: "",
                latitude: 0,
                longitude: 0,
            },
        });
    };

    const editDropOffPlace = (placeName: string) => {
        editTask(index, {
            ...task,
            dropOffPlace: {
                placeName,
                road: "",
                latitude: 0,
                longitude: 0,
            },
        });
    };

    const editPickUpStartTime = (hour: string, minute: string) => {
        editTask(index, {
            ...task,
            pickUpTimeWindow: {
                ...task.pickUpTimeWindow,
                startTimeLimited: {
                    hour,
                    minute,
                },
            },
        });
    };

    const editPickUpEndTime = (hour: string, minute: string) => {
        editTask(index, {
            ...task,
            pickUpTimeWindow: {
                ...task.pickUpTimeWindow,
                endTimeLimited: {
                    hour,
                    minute,
                },
            },
        });
    };

    const editDropOffStartTime = (hour: string, minute: string) => {
        editTask(index, {
            ...task,
            dropOffTimeWindow: {
                ...task.dropOffTimeWindow,
                startTimeLimited: {
                    hour,
                    minute,
                },
            },
        });
    };

    const editDropOffEndTime = (hour: string, minute: string) => {
        editTask(index, {
            ...task,
            dropOffTimeWindow: {
                ...task.dropOffTimeWindow,
                endTimeLimited: {
                    hour,
                    minute,
                },
            },
        });
    };

    const editPickUpServiceTime = (minute: string) => {
        editTask(index, {
            ...task,
            pickUpServiceTime: minute,
        });
    };

    const editDropOffServiceTime = (minute: string) => {
        editTask(index, {
            ...task,
            dropOffServiceTime: minute,
        });
    };

    const editCapa = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (isNaN(Number(value))) {
            return;
        }
        editTask(index, {
            ...task,
            capacity: Number(value),
        });
    };

    const handleChangePickUpServiceTime = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;
        if (isNaN(Number(value))) {
            return;
        }
        editPickUpServiceTime(value);
    };

    const handleChangeDropOffServiceTime = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;
        if (isNaN(Number(value))) {
            return;
        }
        editDropOffServiceTime(value);
    };

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: task.taskName });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <TableRow ref={setNodeRef} style={style}>
            <TableData>
                <FirstContainer>
                    <DragHandle {...attributes} {...listeners}>
                        <DragHandleIcon />
                    </DragHandle>
                    <Header>{task.taskName}</Header>
                </FirstContainer>
            </TableData>
            <TableData>
                <SecondContainer>
                    <FirstBottomLineBox>
                        <PickUpTitle>상차</PickUpTitle>
                    </FirstBottomLineBox>
                    <FirstNoneBottomLineBox>
                        <DropOffTitle>하차</DropOffTitle>
                    </FirstNoneBottomLineBox>
                </SecondContainer>
            </TableData>
            <TableData>
                <MiddleContainer>
                    <BottomLineBox>
                        <RoutingServicePostCodeButton
                            disabled={pickUpAddressDiabled}
                            setAddress={editPickUpPlace}
                            text={task.pickUpPlace.placeName}
                            disabledText={task.pickUpPlace.placeName || "주소를 검색하세요"}
                        />
                    </BottomLineBox>
                    <NoneBottomLineBox>
                        <RoutingServicePostCodeButton
                            disabled={false}
                            setAddress={editDropOffPlace}
                            text={task.dropOffPlace.placeName}
                            disabledText={task.dropOffPlace.placeName || "주소를 검색하세요"}
                        />
                    </NoneBottomLineBox>
                </MiddleContainer>
            </TableData>
            <TableData>
                <MiddleContainer>
                    <BottomLineBox>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "12px",
                            }}
                        >
                            <RoutingServiceTime2
                                time={task.pickUpTimeWindow.startTimeLimited}
                                editTime={editPickUpStartTime}
                                disabled={pickUpAddressDiabled}
                            />
                            <Tilde>~</Tilde>
                            <RoutingServiceTime2
                                time={task.pickUpTimeWindow.endTimeLimited}
                                editTime={editPickUpEndTime}
                                disabled={pickUpAddressDiabled}
                            />
                        </div>
                    </BottomLineBox>
                    <NoneBottomLineBox>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "12px",
                            }}
                        >
                            <RoutingServiceTime2
                                time={task.dropOffTimeWindow.startTimeLimited}
                                editTime={editDropOffStartTime}
                                disabled={false}
                            />
                            <div
                                style={{
                                    width: "10px",
                                }}
                            >
                                <Tilde>~</Tilde>
                            </div>
                            <RoutingServiceTime2
                                time={task.dropOffTimeWindow.endTimeLimited}
                                editTime={editDropOffEndTime}
                                disabled={false}
                            />
                        </div>
                    </NoneBottomLineBox>
                </MiddleContainer>
            </TableData>
            <TableData>
                <ServiceTimeContainer>
                    <LastBottomLineBox>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "8px",
                            }}
                        >
                            <GeneralTextField
                                name={"pickUpServiceTime"}
                                placeholder="0"
                                value={task.pickUpServiceTime}
                                onChange={handleChangePickUpServiceTime}
                                width="42px"
                                disabled={pickUpAddressDiabled}
                            />
                            분
                        </div>
                    </LastBottomLineBox>
                    <LastNoneBottomLineBox>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "8px",
                            }}
                        >
                            <GeneralTextField
                                name={"dropOffServiceTime"}
                                placeholder="0"
                                value={task.dropOffServiceTime}
                                onChange={handleChangeDropOffServiceTime}
                                width="42px"
                                disabled={false}
                            />
                            분
                        </div>
                    </LastNoneBottomLineBox>
                </ServiceTimeContainer>
            </TableData>
            <TableData>
                <LastContainer>
                    <LoadContainer>
                        <GeneralTextField
                            name={"load"}
                            value={task.capacity}
                            onChange={editCapa}
                            width="42px"
                        />
                    </LoadContainer>
                    <ButtonContent>
                        {showDelete && (
                            <div onClick={() => clickDelete(index)}>
                                <DeleteIcon />
                            </div>
                        )}
                        {showAdd && (
                            <div onClick={clickAdd}>
                                <AddIcon />
                            </div>
                        )}
                    </ButtonContent>
                </LastContainer>
            </TableData>
        </TableRow>
    );
};

const TableRow = styled.tr`
  & > td:first-child {
    border-top: 1px solid ${theme.palette.primitives.gray200};
    border-left: 1px solid ${theme.palette.primitives.gray200};
    border-bottom: 1px solid ${theme.palette.primitives.gray200};
    box-sizing: border-box;
    border-radius: 12px 0 0 12px;
  }

  & > td:last-child {
    border-top: 1px solid ${theme.palette.primitives.gray200};
    border-right: 1px solid ${theme.palette.primitives.gray200};
    border-bottom: 1px solid ${theme.palette.primitives.gray200};
    box-sizing: border-box;
    border-radius: 0 12px 12px 0;
  }
`;

const TableData = styled.td`
  align: center;
  vertical-align: top;
  // height: 154px;
  padding: 22px 0 19px 0;
  border-top: 1px solid ${theme.palette.primitives.gray200};
  border-bottom: 1px solid ${theme.palette.primitives.gray200};
  box-sizing: border-box;
`;

const FirstContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 2px;
  margin: 0 20px 0 16px;
  box-sizing: border-box;
  gap: 8px;
`;

const SecondContainer = styled.div`
  display: flex;
  height: 118px;
  flex-direction: column;
  // justify-content: center;
  // align-items: center;
  // padding: 2px;
  // margin: 0 30px 0 10px;
  box-sizing: border-box;
`;

const MiddleContainer = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  box-sizing: border-box;
`;

const ServiceTimeContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  padding: 0 30px 0 0;
  box-sizing: border-box;
`;

const LastContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 2px;
  margin: 0 16px 0 16px;
  box-sizing: border-box;
`;

const Header = styled.div`
  width: 46px;
  height: 113px;
  width: max-content;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${theme.palette.primitives.gray800};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const LoadContainer = styled.div`
  height: 113px;
  display: flex;
  width: fit-content;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 9px;
`;

const PickUpTitle = styled.div`
  width: max-content;
  height: 58px;
  padding: 13px 0 0 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  font-size: 15px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  color: ${theme.palette.primitives.gray600};
  box-sizing: border-box;
`;

const DropOffTitle = styled.div`
  width: max-content;
  height: 58px;
  padding: 0 0 13px 0;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  font-size: 15px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  color: ${theme.palette.primitives.gray600};
  box-sizing: border-box;
`;

const BottomLineBox = styled.div`
  display: flex;
  width: 100%;
  height: 60px;
  justify-content: center;
  align-items: flex-start;
  border-bottom: 1px solid #ebeef3;
  box-sizing: border-box;
  margin: 0 30px;
  padding: 0 0 0 2px;
`;

const FirstBottomLineBox = styled.div`
  display: flex;
  width: 100%;
  height: 60px;
  border-bottom: 1px solid #ebeef3;
  box-sizing: border-box;
  padding: 0 0 0 2px;
  margin: 0 30px 0 0;
`;

const LastBottomLineBox = styled.div`
  display: flex;
  width: 100%;
  height: 60px;
  justify-content: flex-end;
  align-items: flex-start;
  border-bottom: 1px solid #ebeef3;
  box-sizing: border-box;
  padding: 2px 0 0 32px;
`;

const NoneBottomLineBox = styled.div`
  display: flex;
  width: 100%;
  height: 59px;
  justify-content: center;
  align-items: flex-end;
  box-sizing: border-box;
  margin: 0 30px;
  padding: 0 0 0 2px;
`;

const FirstNoneBottomLineBox = styled.div`
  display: flex;
  width: 100%;
  height: 59px;
  box-sizing: border-box;
  padding: 0 0 0 2px;
  margin: 0 30px 0 0;
`;

const LastNoneBottomLineBox = styled.div`
  display: flex;
  width: 100%;
  height: 59px;
  justify-content: flex-end;
  align-items: flex-end;
  box-sizing: border-box;
  padding: 0 0 2px 32px;
`;

const Tilde = styled.div`
  padding: 0;
  margin: 0;
  color: #979da8;
  font-size: 15px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  width: 10px;
`;

const ButtonContent = styled.div`
  display: flex;
  height: 56px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  position: absolute;
  top: 32px;
  left: 88px;
  padding: 0 34px 0 0;
  box-sizing: border-box;
  z-index: 999;
`;

// 드래그 중인 아이템 스타일링
const DragOverlay = styled.div`
  box-shadow: 0 0 0 1px rgba(63, 63, 68, 0.05),
             0 1px 3px 0 rgba(34, 33, 81, 0.15);
`;

// 드래그 핸들 스타일링
const DragHandle = styled.div`
  cursor: move;
  display: flex;
  align-items: center;
  padding: 0 8px;
  color: ${theme.palette.primitives.gray400};
  
  &:hover {
    color: ${theme.palette.primitives.gray600};
  }
`;
